import type { TemplatePrompts } from '@kimuno/api-sdk/lib/types/common/Conversation';

import { type AiAssistantPageSource, aiAssistantPageSource, routes } from '../../../constants';
import type { User } from '../../../types/User';
import { LAUNCH_PARAM_KEYS } from '../../AIAssistant/shared/templateLaunchParams';

export interface MukiCardLaunch {
	to: {
		pathname: string;
		query: Record<string, string>;
		state: { pageSource: AiAssistantPageSource };
	};
	subject?: string;
}

// djb2 — a deterministic, non-cryptographic string hash. Same seed always yields
// the same index, so a card's daily subject/class pick is stable across rerenders
// and reloads (no flicker) yet rotates once the date component of the seed changes.
const hashString = (input: string): number => {
	let hash = 5381;
	for (let i = 0; i < input.length; i++) {
		hash = ((hash << 5) + hash + input.charCodeAt(i)) >>> 0;
	}
	return hash;
};

const pickDaily = <T>(items: T[], seed: string): T | undefined =>
	items.length ? items[hashString(seed) % items.length] : undefined;

export const getDateKey = (date: Date = new Date()): string => date.toISOString().slice(0, 10);

/**
 * Build a personalized `/ki-tools` launch link for a Muki action card. Picks one
 * subject from the teacher's profile via a daily-seeded rotation (stable within a
 * day, rotates at midnight) and omits it when the teacher has none, so the launch
 * degrades to a bare template pre-select. The chosen `subject` is returned alongside
 * the link for card-label personalization. Grade/class year is intentionally not
 * prefilled — only the subject is.
 */
export const getMukiCardLaunch = (
	user: User,
	templateId: TemplatePrompts,
	dateKey: string
): MukiCardLaunch => {
	const subjectNames = (user.subjectsFlattened ?? []).map(({ text }) => text).filter(Boolean);

	const subject = pickDaily(subjectNames, `${dateKey}:${templateId}:subject`);

	const query: Record<string, string> = { [LAUNCH_PARAM_KEYS.template]: templateId };
	if (subject) query[LAUNCH_PARAM_KEYS.subject] = subject;
	// The AiAssistant Opened emission on /ki-tools reads this back out so a
	// template launch from the dashboard reports `page source = dashboard`
	// instead of the pathname fallback (`mukibude`). Query survives new tab,
	// state covers same-tab — the same propagation the material cards use.
	query.page_source = aiAssistantPageSource.dashboard;

	return {
		to: {
			pathname: `/${routes.aiTools.root}`,
			query,
			state: { pageSource: aiAssistantPageSource.dashboard },
		},
		subject,
	};
};
